import { aiApi, aiSkillsApi } from '../../../lib/apiClient'
import type { MinimalMessage } from '../../../types/aiSkills'
import { parseKeyArray, parseSkillDto } from './skillResponseParsing'

// ── Lookup 1: Pre-loop - select best matching skills for this session ──────────

export async function selectSkillsForSession(
  userMessage: string,
  connectorTypeId?: number,
): Promise<string> {
  try {
    const headers = await aiSkillsApi.getHeaders(connectorTypeId)
    if (!headers.length) return ''

    const listText = headers.map(h => `- ${h.key}: ${h.label} - ${h.description}`).join('\n')
    const prompt = `You are a skill selector for an AI tool-building assistant. Pick the most relevant skill keys for the user request. Return a JSON array of key strings. Return [] if none apply.

    User request: "${userMessage}"

    Available skills:
    ${listText}`

    let response = ''
    await aiApi.toolBuilderChatStream([{ role: 'user', content: prompt }], t => { response = t }, undefined, undefined, undefined, 'skill-meta')

    const keys = parseKeyArray(response)
    if (!keys.length) return ''

    const skills = await aiSkillsApi.getContent(keys)
    if (!skills.length) return ''

    return skills.map(s => `[Skill: ${s.label}]\n${s.content}`).join('\n\n')
  } catch {
    return ''
  }
}

// ── Lookup 2: Error-triggered - select skills relevant to a real test error ───

export async function lookupSkillForError(
  errorText: string,
  connectorTypeId?: number,
): Promise<string> {
  try {
    const headers = await aiSkillsApi.getHeaders(connectorTypeId)
    if (!headers.length) return ''

    const listText = headers.map(h => `- ${h.key}: ${h.label} - ${h.description}`).join('\n')
    const prompt = `You are a skill selector for an AI tool-building assistant. Given this error, pick skill keys that may help resolve it. Return a JSON array of key strings. Return [] if none apply.

    Error: "${errorText}"

    Available skills:
    ${listText}`

    let response = ''
    await aiApi.toolBuilderChatStream([{ role: 'user', content: prompt }], t => { response = t }, undefined, undefined, undefined, 'skill-meta')

    const keys = parseKeyArray(response)
    if (!keys.length) return ''

    const skills = await aiSkillsApi.getContent(keys)
    if (!skills.length) return ''

    return skills.map(s => `[Skill: ${s.label}]\n${s.content}`).join('\n\n')
  } catch {
    return ''
  }
}

// ── Trigger 1: Front-loaded - extract skill from the opening exchange ─────────

export function triggerFrontLoadedExtraction(
  messages: MinimalMessage[],
  connectorTypeId?: number,
): void {
  extractAndSave(messages, connectorTypeId).catch(() => { })
}

// ── Trigger 2: Post-success - extract skill after successful tool creation ────

export function triggerSuccessExtraction(
  messages: MinimalMessage[],
  connectorTypeId?: number,
): void {
  extractAndSave(messages, connectorTypeId).catch(() => { })
}

// ── Trigger 3: Error-resolution - extract skill from error+fix exchange ───────

export function triggerErrorResolutionExtraction(
  messages: MinimalMessage[],
  errorText: string,
  connectorTypeId?: number,
): void {
  extractAndSave(messages, connectorTypeId, errorText).catch(() => { })
}

// ── Trigger 4: Blocked error - extract pattern from an error that stopped the agent ──

export function triggerBlockedErrorExtraction(
  messages: MinimalMessage[],
  errorText: string,
  connectorTypeId?: number,
): void {
  extractFromBlockedError(messages, connectorTypeId, errorText).catch(() => { })
}

// ── Internal helpers ──────────────────────────────────────────────────────────

async function extractAndSave(
  messages: MinimalMessage[],
  connectorTypeId: number | undefined,
  errorText?: string,
): Promise<void> {
  const convo = messages.map(m => `${m.role}: ${m.content}`).join('\n')
  const errorSection = errorText ? `\nError that was resolved: ${errorText}` : ''

  const prompt = `You are a skill extractor for an AI tool-building assistant. Review this conversation and decide if there is reusable knowledge worth saving as a skill.

A skill is a concise, reusable instruction that helps build similar tools faster - e.g. auth conventions, specific API patterns, error-resolution patterns. Only extract if the pattern is non-obvious and likely to recur.

If a skill is worth saving, return a single JSON object:
{ "key": "snake_case_id", "label": "Short Name", "description": "One sentence", "content": "The reusable instruction" }

Return null if nothing worth saving.

Conversation:
${convo}${errorSection}`

  let response = ''
  await aiApi.toolBuilderChatStream([{ role: 'user', content: prompt }], t => { response = t }, undefined, undefined, undefined, 'skill-meta')

  const dto = parseSkillDto(response, connectorTypeId)
  if (!dto) return

  await aiSkillsApi.create(dto)
}

async function extractFromBlockedError(
  messages: MinimalMessage[],
  connectorTypeId: number | undefined,
  errorText: string,
): Promise<void> {
  const convo = messages.map(m => `${m.role}: ${m.content}`).join('\n')

  const prompt = `You are a skill extractor for an AI tool-building assistant. A tool was generated and tested, but the test failed with an error that stopped the agent:

Error: "${errorText}"

Review the conversation and the error. Look for patterns in the tool configuration that likely caused this - for example:
- A GET request was generated with a non-empty bodyTemplate (GET requests must never have a body; all filter parameters belong in queryMap using $filter)
- Wrong path structure or missing path segment
- Missing required header for the target API

If there is a reusable rule that could prevent this class of mistake in the future, extract it as a skill.

Return a single JSON object if a skill is worth saving:
{ "key": "snake_case_id", "label": "Short Name", "description": "One sentence", "content": "The reusable rule" }

Return null if nothing worth saving.

Conversation:
${convo}`

  let response = ''
  await aiApi.toolBuilderChatStream([{ role: 'user', content: prompt }], t => { response = t }, undefined, undefined, undefined, 'skill-meta')

  const dto = parseSkillDto(response, connectorTypeId)
  if (!dto) return

  await aiSkillsApi.create(dto)
}
